import {
  HAS_ENTITY,
  INCOME_BANDS,
  LEGACY_INCOME_BAND,
  PRESCRIBER_LICENSE_CODES,
  SECTION_ORDER,
  TAX_TREATMENTS,
} from '../lib/constants'
import type { ComplianceLessonKey, IntakeAnswers } from '../lib/types'
import type { InputType, QuestionDef } from './types'

/** Whether any licensed state's credential carries prescribing scope. */
const prescribes = (answers: IntakeAnswers): boolean =>
  Object.values((answers.license_type ?? {}) as Record<string, unknown>).some(
    (code) => PRESCRIBER_LICENSE_CODES.includes(code as string)
  )

const is = (
  answers: IntakeAnswers,
  key: string,
  ...values: string[]
): boolean => values.includes(answers[key] as string)

/**
 * The full intake question set (Entry Intake v5), keyed by the backend
 * `questionKey`. Order within a section is the display order. Add/edit a
 * question here — the key stays the contract with the API.
 */
export const QUESTIONS: QuestionDef[] = [
  // ── Your license ───────────────────────────────────────────────────────────
  {
    key: 'licensed_states',
    section: 'license',
    prompt: 'Which state or states are you licensed in?',
    help: 'Each license renews on its own schedule, so we track them per state.',
    input: 'states',
    // The entity section runs first, so where the business is registered is a
    // sensible opening guess for where its owner is licensed. A starting
    // point, not an assertion: it stops following the moment it's edited, and
    // `practice_states` chains off this one in turn.
    prepopulateFrom: 'entity_registration_state',
  },
  {
    key: 'license_type',
    section: 'license',
    prompt: 'What license do you hold in each state?',
    help: 'The credential name can differ by state. A counselor is LMHC in WA, LPC in TX, LPCC in CA.',
    input: 'per_state_select',
    reveal: (a) => Boolean((a.licensed_states as string[] | undefined)?.length),
    // Alphabetical, with the catch-all last — matches the per-state list the
    // backend catalog serves, which is what actually renders once it loads.
    options: [
      { value: 'LCSW', label: 'LCSW' },
      { value: 'LICSW', label: 'LICSW' },
      { value: 'LMFT', label: 'LMFT' },
      { value: 'LMHC', label: 'LMHC' },
      { value: 'LPC', label: 'LPC' },
      { value: 'LPCC', label: 'LPCC' },
      { value: 'PSY', label: 'Psychologist' },
      { value: 'OTHER', label: 'Other licensed professional' },
    ],
  },
  {
    key: 'license_expires_at',
    section: 'license',
    prompt: 'When does your license expire?',
    help: "Your renewal date is the single most important date in your practice. We'll watch the clock in each state and tell you before it runs out.",
    description: "Totally fine to skip. Most people don't know it offhand.",
    input: 'per_state_date',
    dateBound: 'future',
    optional: true,
    reveal: (a) => Boolean((a.licensed_states as string[] | undefined)?.length),
  },
  {
    key: 'practice_states',
    section: 'license',
    prompt: 'In which state or states do you provide care?',
    description: 'Where your clients are located when you see them.',
    input: 'states',
    prepopulateFrom: 'licensed_states',
  },
  {
    key: 'offers_telehealth',
    section: 'license',
    prompt: 'Do you see clients over telehealth?',
    help: 'Most states regulate telehealth separately. Registration, training, and consent rules can differ from in-person care.',
    input: 'single',
    columns: 2,
    compact: true,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'prescribes_controlled_substances',
    section: 'license',
    prompt: 'Do you prescribe controlled substances?',
    help: 'Prescribers carry a DEA registration with its own renewal clock.',
    input: 'single',
    columns: 2,
    compact: true,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    // Only prescribers. This originally gated on `profession` and a DEA
    // entry in `additional_credentials`; both were retired, which left it
    // asked of every therapist. `license_type` carries the same distinction —
    // the codes above are the API's `nurse_prescriber` family.
    reveal: prescribes,
  },
  {
    key: 'dea_renews_at',
    section: 'license',
    prompt: 'When does your DEA registration renew?',
    input: 'date',
    dateBound: 'future',
    optional: true,
    // Both conditions: correcting `license_type` away from a prescribing
    // credential closes the parent question but leaves its stored 'yes',
    // which would strand this on screen with nothing explaining it.
    reveal: (a) =>
      prescribes(a) && is(a, 'prescribes_controlled_substances', 'yes'),
  },
  // ── Your practice ──────────────────────────────────────────────────────────
  {
    key: 'client_status',
    section: 'practice_stage',
    prompt: 'Are you seeing private-practice clients yet?',
    help: 'A live caseload decides which obligations apply now vs. later.',
    input: 'single',
    columns: 2,
    options: [
      { value: 'yes', label: 'Yes' },
      {
        value: 'notyet',
        label: 'Not yet',
        sublabel: 'Still setting up or launching soon',
      },
    ],
  },
  {
    // Only asked once there's a practice to date — a "not yet" answer has no
    // launch date to give, so this is skipped rather than left optional.
    key: 'practice_launched_at',
    section: 'practice_stage',
    prompt: 'When did you launch your private practice?',
    description: 'An approximate date is fine.',
    input: 'date',
    dateBound: 'past',
    recentPastDate: true,
    reveal: (a) => is(a, 'client_status', 'yes'),
  },
  {
    key: 'weekly_caseload',
    section: 'practice_stage',
    prompt: 'How many clients are you seeing a week through your practice?',
    description: 'A rough number is fine.',
    input: 'number',
    unit: 'clients a week',
    numberSlider: { min: 0, max: 60, step: 1 },
    reveal: (a) => is(a, 'client_status', 'yes'),
  },
  {
    key: 'expected_income_band',
    section: 'practice_stage',
    prompt: 'Roughly what practice income do you expect over the next year?',
    help: 'A rough estimate is enough. It decides whether quarterly taxes apply and when an S corporation starts to make sense.',
    input: 'income_band_slider',
    // Legacy band last and unreachable from the slider — retained so a stored
    // pre-split answer renders its label instead of the raw code.
    options: [
      ...Object.entries(INCOME_BANDS).map(([value, label]) => ({
        value,
        label,
      })),
      ...Object.entries(LEGACY_INCOME_BAND).map(([value, label]) => ({
        value,
        label,
      })),
    ],
  },
  {
    key: 'practice_size',
    section: 'practice_stage',
    prompt: 'How many people work in your practice, including you?',
    help: 'Solo and group practices carry different obligations, so this shapes what we track for you.',
    input: 'single',
    columns: 3,
    // The values are `NumberOfEmployees` band names, and those bands count
    // employees and contractors *excluding* self ('2-10' is 1–9 of them, '10+'
    // is 10 or more) — while this question asks headcount *including* self. So
    // the labels are deliberately offset by one from the value strings: a
    // 10-person practice is 9 employees, which belongs in '2-10', and only 11+
    // people reaches the '10+' band that routes to the waitlist. Labelling
    // these '2–9' / '10 or more' would waitlist a 9-employee practice the main
    // signup flow lets through.
    options: [
      { value: '1', label: 'Just me' },
      { value: '2-10', label: '2–10' },
      { value: '10+', label: 'More than 10' },
    ],
  },
  {
    key: 'hiring_plan',
    section: 'practice_stage',
    prompt: 'Are you planning to hire any 1099 contractors or W-2 employees?',
    help: 'A team adds payroll, and payroll adds filings.',
    input: 'single',
    columns: 2,
    // Only asked for solo practices — anyone who already answered practice_size
    // as 2-10 or 10+ already told us they have people; DERIVED_ANSWERS below
    // fills this in as 'group' for them instead of asking again.
    reveal: (a) => is(a, 'practice_size', '1'),
    options: [
      { value: 'solo', label: 'Not planning to' },
      { value: 'somepoint', label: 'Maybe eventually' },
      { value: 'year', label: 'Yes, within the next year' },
    ],
  },
  {
    key: 'has_multiple_owners',
    section: 'practice_stage',
    prompt: 'Does your practice have multiple owners?',
    input: 'single',
    columns: 2,
    compact: true,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    reveal: (a) => Boolean(a.hiring_plan) && !is(a, 'hiring_plan', 'solo'),
  },

  // ── Your business & tax entity ─────────────────────────────────────────────
  {
    key: 'tax_treatment',
    section: 'entity',
    prompt: 'How is your practice taxed?',
    help: "Unless you filed an election with the IRS, you're a sole proprietor by default. That's true even if you registered your practice as a business with your state. Your tax structure decides which returns you owe.",
    input: 'single',
    columns: 2,
    options: [
      {
        value: TAX_TREATMENTS.soleprop,
        label: 'Sole proprietor',
        sublabel: 'Most new practices start here.',
      },
      {
        value: TAX_TREATMENTS.scorp,
        label: 'S corporation',
        sublabel: 'You run payroll for yourself.',
      },
      {
        value: TAX_TREATMENTS.ccorp,
        label: 'C corporation',
        sublabel: 'Uncommon for solo practices.',
      },
      {
        value: TAX_TREATMENTS.other,
        label: 'Other / not sure',
        sublabel: "We'll help you figure it out.",
      },
    ],
  },
  {
    key: 'has_entity',
    section: 'entity',
    prompt: 'Is your practice registered as an LLC, PLLC, or PC?',
    help: 'This is your business registration with your state, not your professional license or your tax setup. Many registered practices are still taxed as sole proprietors.',
    input: 'single',
    columns: 3,
    options: [
      {
        value: HAS_ENTITY.yes,
        label: 'Yes',
        sublabel: 'You registered your practice as a business with your state.',
      },
      {
        value: HAS_ENTITY.no,
        label: 'No',
        sublabel: "You've only ever practiced under your own name.",
      },
      {
        value: HAS_ENTITY.notsure,
        label: "I'm not sure",
        sublabel: "We'll help you check.",
      },
    ],
    reveal: (a) =>
      Boolean(a.tax_treatment) &&
      !is(a, 'tax_treatment', TAX_TREATMENTS.scorp, TAX_TREATMENTS.ccorp),
  },
  {
    key: 'entity_registration_state',
    section: 'entity',
    prompt: 'In which state is your business entity registered?',
    input: 'state',
    reveal: (a) => is(a, 'has_entity', HAS_ENTITY.yes),
  },
  {
    key: 'entity_formed_at',
    section: 'entity',
    prompt: 'When was your entity formed?',
    help: 'Your entity is the business you registered with your state, such as an LLC, PLLC, or PC. The formation date is the one on the approval letter the state sent back.',
    input: 'date',
    dateBound: 'past',
    optional: true,
    reveal: (a) => is(a, 'has_entity', HAS_ENTITY.yes),
  },
  {
    key: 'scorp_intent',
    section: 'entity',
    prompt: 'Are you thinking about forming an S corporation?',
    help: 'An S corporation is a tax election you make for an entity you already have, such as an LLC or a PC. It changes how your profit is taxed and adds payroll.',
    input: 'single',
    options: [
      { value: 'soon', label: 'Yes, soon' },
      { value: 'someday', label: 'Someday, just curious' },
      { value: 'no', label: 'No, not right now' },
    ],
    reveal: (a) =>
      Boolean(a.tax_treatment) && !is(a, 'tax_treatment', TAX_TREATMENTS.scorp),
  },

  // ── Your protection ────────────────────────────────────────────────────────
  {
    key: 'malpractice_coverage',
    section: 'insurance',
    prompt: 'Do you have malpractice insurance?',
    help: "it's the one gap that can't wait if you're seeing clients.",
    input: 'single',
    columns: 3,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'notsure', label: 'Not sure' },
    ],
  },
  {
    key: 'malpractice_renews_at',
    section: 'insurance',
    prompt: 'When does it renew?',
    input: 'date',
    dateBound: 'future',
    optional: true,
    reveal: (a) => is(a, 'malpractice_coverage', 'yes'),
  },

  // ── Your money (hidden if not yet seeing clients) ─────────────────────────
  {
    key: 'payer_mix',
    section: 'getting_paid',
    prompt: 'How do clients pay for sessions?',
    help: 'Private pay vs. insurance changes everything downstream: billing, records, even which bank products fit.',
    input: 'single',
    columns: 2,
    options: [
      { value: 'private', label: 'Private pay only' },
      { value: 'insurance', label: 'Mostly insurance' },
      { value: 'mix', label: 'A mix of both' },
      {
        value: 'platform',
        label: 'A platform handles it',
        sublabel: 'Headway, Alma, Grow…',
      },
    ],
    reveal: (a) => a.client_status !== 'notyet',
  },
  {
    key: 'payment_methods',
    section: 'getting_paid',
    prompt: 'How do you collect payments?',
    description: 'Select all that apply.',
    input: 'multi',
    optional: true,
    options: [
      { value: 'ehr', label: 'Through my EHR' },
      { value: 'app', label: 'Therapist payment app' },
      { value: 'card', label: 'Card processor' },
      { value: 'venmo', label: 'Venmo / Zelle / cash' },
      { value: 'platform', label: 'Platform handles it' },
      { value: 'notset', label: 'Not really set up yet' },
    ],
    reveal: (a) => is(a, 'payer_mix', 'private', 'mix'),
  },
  {
    key: 'billing_method',
    section: 'getting_paid',
    prompt: 'How do you handle insurance claims and billing?',
    help: 'Billing is a system, not a chore. Knowing who runs yours tells us where the load sits.',
    input: 'single',
    options: [
      { value: 'self', label: 'I do it myself' },
      { value: 'biller', label: 'An individual biller' },
      { value: 'service', label: 'A billing service' },
      { value: 'ehr', label: 'Through my EHR / clearinghouse' },
      { value: 'platform', label: 'Platform handles it' },
      { value: 'want', label: 'Not paneled yet, want to be' },
    ],
    reveal: (a) => is(a, 'payer_mix', 'insurance', 'mix'),
  },
  {
    key: 'insurance_panels',
    section: 'getting_paid',
    prompt: 'Which insurance panels are you on?',
    description:
      "Optional. Each panel becomes a tracked credentialing item on your dashboard. We'll ask about dates later, when they're easier to find.",
    input: 'multi',
    optional: true,
    columns: 2,
    options: [
      { value: 'medicaid', label: 'Medicaid' },
      { value: 'medicare', label: 'Medicare' },
      { value: 'bcbs', label: 'Blue Cross / Blue Shield' },
      { value: 'aetna', label: 'Aetna' },
      { value: 'cigna', label: 'Cigna' },
      { value: 'uhc', label: 'UnitedHealthcare / Optum' },
      { value: 'eap', label: 'EAPs' },
      { value: 'other', label: 'Other' },
    ],
    reveal: (a) => is(a, 'payer_mix', 'insurance', 'mix'),
  },
  {
    key: 'has_business_bank_account',
    section: 'getting_paid',
    prompt: 'Do you have a separate business bank account set up?',
    help: 'Separating business and personal money is the first layer of clean books.',
    input: 'single',
    columns: 2,
    options: [
      {
        value: 'yes',
        label: 'Yes',
        sublabel: 'A dedicated business account',
      },
      { value: 'no', label: 'No', sublabel: 'One account for everything' },
    ],
  },
  {
    key: 'business_bank',
    section: 'getting_paid',
    prompt: 'Which business bank do you use today?',
    input: 'single',
    columns: 3,
    // Every named bank below resolves to a real logo through
    // `institutionLogoMap`. "Local bank or credit union" and "Other" are not
    // brands, so they carry no badge at all.
    optionBadge: 'logo',
    options: [
      { value: 'chase', label: 'Chase' },
      { value: 'bofa', label: 'Bank of America' },
      { value: 'wells', label: 'Wells Fargo' },
      { value: 'citi', label: 'Citibank' },
      { value: 'capitalone', label: 'Capital One' },
      { value: 'usbank', label: 'U.S. Bank' },
      { value: 'bluevine', label: 'Bluevine' },
      { value: 'novo', label: 'Novo' },
      { value: 'mercury', label: 'Mercury' },
      { value: 'relay', label: 'Relay' },
      { value: 'local', label: 'Local bank or credit union' },
      { value: 'other', label: 'Other' },
    ],
    reveal: (a) => is(a, 'has_business_bank_account', 'yes'),
  },
  {
    key: 'practice_mgmt_software',
    section: 'getting_paid',
    prompt: 'Which EHR or practice-management system do you use today?',
    input: 'single',
    // "Other" and "None yet" are categories rather than brands, so they carry
    // no badge, the same way the bank question's do.
    optionBadge: 'logo',
    options: [
      { value: 'simplepractice', label: 'SimplePractice' },
      { value: 'therapynotes', label: 'TherapyNotes' },
      { value: 'jane', label: 'Jane' },
      { value: 'other', label: 'Other' },
      { value: 'none', label: 'None yet' },
    ],
  },

  // money_books merged into the "Your money" (getting_paid) section — kept
  // as its own question here, just re-sectioned.
  {
    key: 'money_system',
    section: 'getting_paid',
    prompt: 'How do you handle the money side today?',
    help: 'Honest answers get better reports. "It\'s a mess" is the most common answer, and the most fixable.',
    input: 'single',
    // Ordered from most to least hands-on, with the two "no real system"
    // answers last. Display order only; the API's enum is a set, not a sequence.
    options: [
      { value: 'diy', label: 'Spreadsheet / DIY' },
      { value: 'software', label: 'Software' },
      { value: 'accountant', label: 'An accountant or bookkeeper' },
      { value: 'mess', label: "Honestly, it's a mess" },
      { value: 'nothing', label: 'Nothing yet' },
    ],
  },
]

/**
 * Answers for questions `reveal` hides still need a value downstream — this
 * fills them in without asking, keyed by the question whose answer to derive.
 * Never overrides an answer the user actually gave.
 */
const DERIVED_ANSWERS: Record<
  string,
  (a: IntakeAnswers) => string | undefined
> = {
  // hiring_plan is only asked for solo practices (practice_size === '1') —
  // anyone who said 2-10 or 10+ already told us they have people, so this
  // fills in the same 'group' value the obligations engine expects instead
  // of asking again.
  hiring_plan: (a) => {
    const size = a.practice_size
    return typeof size === 'string' && size !== '1' ? 'group' : undefined
  },
  // An S or C corporation election requires an entity, so the answer is already
  // known and the question is not shown to them. Same shape as hiring_plan:
  // filled rather than asked, and never overriding a stored answer.
  has_entity: (a) =>
    is(a, 'tax_treatment', TAX_TREATMENTS.scorp, TAX_TREATMENTS.ccorp)
      ? HAS_ENTITY.yes
      : undefined,
}

/** Applies DERIVED_ANSWERS on top of the real answers — the single place
 *  every reader (visibility, completeness, submission) should go through. */
export const withDerivedAnswers = (answers: IntakeAnswers): IntakeAnswers => {
  let next = answers
  for (const [key, derive] of Object.entries(DERIVED_ANSWERS)) {
    if (next[key] !== undefined) continue
    const derived = derive(answers)
    if (derived === undefined) continue
    if (next === answers) next = { ...answers }
    next[key] = derived
  }
  return next
}

/** Questions in a section that are currently revealed by the answers so far. */
export const visibleQuestions = (
  section: ComplianceLessonKey,
  answers: IntakeAnswers
): QuestionDef[] =>
  QUESTIONS.filter(
    (q) => q.section === section && (!q.reveal || q.reveal(answers))
  )

/** A question's option label, by key and stored value — so prose that names an
 *  answer reads from the registry instead of restating its labels. Falls back to
 *  the raw value for a key or option the registry doesn't carry. */
export const optionLabelFor = (key: string, value: string): string =>
  QUESTIONS.find((q) => q.key === key)?.options?.find((o) => o.value === value)
    ?.label ?? value

export const isAnswered = (value: unknown): boolean => {
  if (value === undefined || value === null) return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim().length > 0
  return true
}

const PER_STATE_INPUTS = new Set<InputType>([
  'per_state_date',
  'per_state_select',
])

/** One control per licensed state, so holding *a* value isn't answered: every
 *  state needs one. An explicit skip stores `null`, which counts. */
export const everyStateAnswered = (
  value: unknown,
  answers: IntakeAnswers
): boolean => {
  const states = (answers.licensed_states as string[] | undefined) ?? []
  const byState = (value ?? {}) as Record<string, unknown>
  return states.length > 0 && states.every((s) => byState[s] !== undefined)
}

/** Answered-ness for one question, which for a per-state input means every
 *  licensed state, not merely some. Submit rejects a licensed state with no
 *  credential, so anything looser advances the user into a 400. */
export const isQuestionAnswered = (
  def: QuestionDef,
  answers: IntakeAnswers
): boolean =>
  PER_STATE_INPUTS.has(def.input)
    ? everyStateAnswered(answers[def.key], answers)
    : isAnswered(answers[def.key])

/** A section is complete when every required, revealed question is answered. */
export const isSectionComplete = (
  section: ComplianceLessonKey,
  answers: IntakeAnswers
): boolean =>
  visibleQuestions(section, answers).every(
    (q) => q.optional || isQuestionAnswered(q, answers)
  )

/**
 * The sections, in order. Nothing is hidden wholesale: "Your money" used to
 * disappear until there were clients, which also skipped the questions that do
 * apply before a first one — `money_system` among them, which projects onto
 * the derived profile and can't be backfilled for someone nobody asked.
 *
 * `payer_mix` carries that gate instead, and `payment_methods`,
 * `billing_method` and `insurance_panels` follow it, since each already
 * reveals off a `payer_mix` answer.
 */
export const navSections = (): readonly ComplianceLessonKey[] => SECTION_ORDER
